// Mutation hooks for upsert and approve operations
import { useState, useCallback } from 'react';
import type {
  OpportunityAdmin,
  UpsertOpportunityPayload,
  UpsertOpportunityResponse,
} from '../types';
import { opportunitiesService, type ApiError } from '../opportunities.service';

export interface MutationState {
  loading: boolean;
  error: ApiError | null;
}

// Return shape for all mutations — data is null on failure, error is null on success
export interface MutationResult<T> {
  data: T | null;
  error: ApiError | null;
}

// Upsert (create or update by link) — used by admin to manually add opportunities
export function useUpsertOpportunity() {
  const [state, setState] = useState<MutationState>({ loading: false, error: null });

  const upsert = useCallback(
    async (
      payload: UpsertOpportunityPayload
    ): Promise<MutationResult<UpsertOpportunityResponse>> => {
      setState({ loading: true, error: null });
      try {
        const data = await opportunitiesService.upsert(payload);
        setState({ loading: false, error: null });
        return { data, error: null };
      } catch (err) {
        const error = err as ApiError;
        setState({ loading: false, error });
        return { data: null, error };
      }
    },
    []
  );

  return { ...state, upsert };
}

// Update — admin only, edits an existing opportunity by id via PATCH
export function useUpdateOpportunity() {
  const [state, setState] = useState<MutationState>({ loading: false, error: null });

  const update = useCallback(
    async (
      id: string,
      payload: UpsertOpportunityPayload
    ): Promise<MutationResult<UpsertOpportunityResponse>> => {
      setState({ loading: true, error: null });
      try {
        const data = await opportunitiesService.update(id, payload);
        setState({ loading: false, error: null });
        return { data, error: null };
      } catch (err) {
        const error = err as ApiError;
        setState({ loading: false, error });
        return { data: null, error };
      }
    },
    []
  );

  return { ...state, update };
}

// Delete — admin only, permanently removes an opportunity by id
export function useDeleteOpportunity() {
  const [state, setState] = useState<MutationState>({ loading: false, error: null });

  const deleteById = useCallback(async (id: string): Promise<MutationResult<true>> => {
    setState({ loading: true, error: null });
    try {
      await opportunitiesService.delete(id);
      setState({ loading: false, error: null });
      return { data: true, error: null };
    } catch (err) {
      const error = err as ApiError;
      setState({ loading: false, error });
      return { data: null, error };
    }
  }, []);

  return { ...state, deleteById };
}

// Approve — admin only, sets status=APPROVED on the given opportunity
export function useApproveOpportunity() {
  const [state, setState] = useState<MutationState>({ loading: false, error: null });

  const approve = useCallback(async (id: string): Promise<MutationResult<OpportunityAdmin>> => {
    setState({ loading: true, error: null });
    try {
      const data = await opportunitiesService.approve(id);
      setState({ loading: false, error: null });
      return { data, error: null };
    } catch (err) {
      const error = err as ApiError;
      setState({ loading: false, error });
      return { data: null, error };
    }
  }, []);

  return { ...state, approve };
}
